import React, { useCallback, useMemo, useState } from "react";
import { Grid, SwipeableDrawer } from "@mui/material";
import { StyledDrawerTnC, StyledLogin } from "./styled";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import Otp from "./Otp";
import { useDispatch, useSelector } from "react-redux";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import { ClipLoader } from "react-spinners";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getEncipherencode,
  hasValidEncipherencode,
} from "utils/encipherencode";
import Footer from "components/v1/common/Footer";
import { LazyLoadImage } from "react-lazy-load-image-component";
import LoginForm from "./LoginForm";
import LoginLayoutOverlay from "./LoginLayoutOverlay";
import LoginLayoutSplit from "./LoginLayoutSplit";
import TermsContent from "./TermsContent";
import useLoginFlow from "./useLoginFlow";
import LoginErrorBoundary from "./LoginErrorBoundary";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function Login({ loginLoader, setLoginLoader, loadConfigurations }) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm();
  const merchantConfig = useSelector(getMerchantConfig);
  const dispatch = useDispatch();
  const rmsConfig = useSelector(getRmsConfig);
  const [params] = useSearchParams();
  const encipherencode = getEncipherencode(params);
  const navigate = useNavigate();
  const [tnc, setTnc] = useState(false);
  const {
    confirmOtp,
    setConfirmOtp,
    inputValue,
    response,
    setResponse,
    otpSendError,
    otpLoading,
    loginFieldContent,
    loginHandler,
  } = useLoginFlow({
    rmsConfig,
    params,
    encipherencode,
    dispatch,
    navigate,
    setValue,
    setLoginLoader,
  });
  const theme = merchantConfig?.public_page_theme_configurations || {};
  const isSplitLayout = theme.cover_image_width === 50;
  const isDesktopTerms = tnc && isSplitLayout && window.innerWidth >= 800;
  const splitDirection =
    theme.login_section_position === "right" ? "row" : "row-reverse";
  const splitJustifyContent = !theme.login_section_brand_logo_visible_in_desktop
    ? theme.login_section_position === "left"
      ? "flex-start"
      : theme.login_section_position === "right"
        ? "flex-end"
        : "center"
    : "center";
  const splitLoginAlignment = theme.cover_image_visible_in_desktop
    ? "center"
    : theme.login_section_position === "left"
      ? "flex-start"
      : theme.login_section_position === "right"
        ? "flex-end"
        : "center";
  const overlayJustifyContent =
    theme.login_section_position === "left"
      ? "flex-start"
      : theme.login_section_position === "center"
        ? "center"
        : "flex-end";
  const overlayAlignment = theme.cover_image_visible_in_desktop
    ? "center"
    : theme.login_section_position === "left"
      ? "flex-start"
      : theme.login_section_position === "right"
        ? "flex-end"
        : "center";
  const showHero =
    theme.cover_image_visible_in_mobile || theme.cover_image_visible_in_desktop;
  const showBrandLogo =
    theme.login_section_brand_logo_visible_in_desktop ||
    theme.login_section_brand_logo_visible_in_mobile;
  const handleOpenTerms = useCallback(() => {
    setTnc(true);
  }, []);
  const handleCloseTerms = useCallback(() => {
    setTnc(false);
  }, []);

  const getHelperText = useCallback(
    (content) => {
      if (!errors.customerIdentifier) {
        return content.helperText;
      }

      return errors.customerIdentifier.type === "pattern"
        ? content.patternErrorText
        : content.requiredErrorText;
    },
    [errors.customerIdentifier],
  );
  const handleLoginSubmit = useMemo(
    () => handleSubmit(loginHandler),
    [handleSubmit, loginHandler],
  );

  const brandImage = useMemo(() => {
    if (merchantConfig && merchantConfig?.brand_cover_image) {
      return (
        <LazyLoadImage
          effect="opacity"
          src={merchantConfig.brand_cover_image}
          alt={merchantConfig.shop_display_name}
          style={{ width: "100%" }}
        />
      );
    }
    return null;
  }, [merchantConfig]);

  const returnToStoreUrl = useMemo(() => {
    if (!merchantConfig?.shopify_domain) {
      return "";
    }

    // Merchant domains can arrive without a protocol; normalize before using them as links.
    if (/^https?:\/\//i.test(merchantConfig.shopify_domain)) {
      return merchantConfig.shopify_domain;
    }

    return `https://${merchantConfig.shopify_domain}`;
  }, [merchantConfig?.shopify_domain]);

  const returnToStoreLink = useMemo(() => {
    if (!returnToStoreUrl || !merchantConfig?.shop_display_name) {
      return null;
    }

    return (
      <a
        className="return-to-store-link"
        href={returnToStoreUrl}
        style={{ background: merchantConfig.merchant_id === 977 ? "#DFE6F7" : `${merchantConfig.primary_theme_color}33` }}
      >
        <span className="return-to-store-arrow" aria-hidden="true">
          <ArrowBackIcon style={{ fontSize: "16px" }} />
        </span>
        <span className="return-to-store-text">
          {`Return to ${merchantConfig.shop_display_name}`}
        </span>
      </a>
    );
  }, [merchantConfig?.shop_display_name, returnToStoreUrl]);

  const splitTextFieldProps = useMemo(
    () => ({
      InputProps: {
        disableUnderline: false,
        sx: {
          "&:before": {
            borderBottom: `1px solid ${merchantConfig.primary_theme_color}`,
          },
          "&:hover:not(.Mui-disabled, .Mui-error):before": {
            borderBottom: `1px solid ${merchantConfig.primary_theme_color}`,
          },
          "&.Mui-focused:after": {
            borderBottom: `2px solid ${merchantConfig.primary_theme_color}`,
          },
          borderRadius: merchantConfig.use_rounded_edge_buttons
            ? "10px"
            : "0px",
        },
      },
    }),
    [
      merchantConfig.primary_theme_color,
      merchantConfig.use_rounded_edge_buttons,
    ],
  );
  const splitButtonStyle = useMemo(
    () => ({
      padding: "10px 1rem",
      fontSize: "16px",
      fontWeight: "600",
      marginTop: "20px",
    }),
    [],
  );
  const buttonSx = useMemo(
    () => ({
      "& .MuiButton-endIcon": {
        marginLeft: "auto",
        marginRight: 0,
      },
    }),
    [],
  );
  const splitTermsTextStyle = useMemo(
    () => ({
      margin: "20px auto",
      color: "#000000B2",
      fontSize: "12px",
    }),
    [],
  );
  const splitTermsLinkStyle = useMemo(
    () => ({
      color: merchantConfig.primary_theme_color,
      textDecoration: "underline",
      cursor: "pointer",
      fontWeight: "600",
    }),
    [merchantConfig.primary_theme_color],
  );
  const overlayTextFieldProps = useMemo(
    () => ({
      sx: {
        "& .MuiOutlinedInput-root input:focus": {
          borderBottom: `2px solid ${merchantConfig.primary_theme_color}`,
          borderRadius: merchantConfig.use_rounded_edge_buttons ? "4px" : "0px",
        },
      },
    }),
    [
      merchantConfig.primary_theme_color,
      merchantConfig.use_rounded_edge_buttons,
    ],
  );
  const overlayButtonStyle = useMemo(
    () => ({
      padding: "10px",
      fontSize: "18px",
      fontWeight: "600",
      margin: "1rem 0px",
    }),
    [],
  );
  const overlayTermsTextStyle = useMemo(
    () => ({
      margin: "20px auto",
      textAlign: "center",
      fontSize: "12px",
    }),
    [],
  );
  const overlayTermsLinkStyle = useMemo(
    () => ({
      color: "#1d8dcc",
      textDecoration: "underline",
      cursor: "pointer",
    }),
    [],
  );
  const desktopTermsProps = useMemo(
    () => ({
      title: t("login.termsTitle"),
      content: rmsConfig.tnc,
      onClose: handleCloseTerms,
      titleStyle: { fontSize: "24px", fontWeight: "600" },
      closeTextStyle: { textAlign: "right", cursor: "pointer" },
      closeIconStyle: {
        width: "24px",
        height: "24px",
        stroke: "#333",
      },
      contentContainerStyle: {
        maxHeight: "75vh",
        overflow: "auto",
        listStylePosition: "outside",
        paddingRight: "10px",
      },
    }),
    [handleCloseTerms, rmsConfig.tnc, t],
  );
  const mobileTermsProps = useMemo(
    () => ({
      title: t("login.termsTitle"),
      content: rmsConfig.tnc,
      onClose: handleCloseTerms,
      titleStyle: { fontSize: "14px", fontWeight: "600" },
      closeTextStyle: {
        textAlign: "right",
        fontSize: "12px",
        cursor: "pointer",
      },
      closeIconStyle: {
        width: "18px",
        height: "18px",
        stroke: "#333",
      },
      contentContainerStyle: {
        maxHeight: "80vh",
        overflow: "auto",
        listStylePosition: "outside",
        paddingRight: "10px",
      },
    }),
    [handleCloseTerms, rmsConfig.tnc, t],
  );

  const splitForm = useMemo(
    () => (
      <Grid
        className="login-section"
        item
        xs={12}
        md={theme.cover_image_visible_in_desktop ? 6 : 12}
        style={{
          alignItems: splitLoginAlignment,
        }}
      >
        {returnToStoreLink}
        {showBrandLogo && (
          <div
            style={{
              maxWidth: "450px",
              width: "100%",
              marginBottom: "1.5rem",
            }}
          >
            <LazyLoadImage
              effect="opacity"
              src={merchantConfig.brand_logo}
              className="brand-logo"
            />
          </div>
        )}
        <LoginForm
          title={rmsConfig.home_page_request_creation_title}
          control={control}
          errors={errors}
          onSubmit={handleLoginSubmit}
          otpLoading={otpLoading}
          otpSendError={otpSendError}
          merchantConfig={merchantConfig}
          fieldContent={loginFieldContent.light}
          variant="standard"
          helperTextResolver={getHelperText}
          brandHomePageNote={rmsConfig.brand_home_page_note}
          onOpenTerms={handleOpenTerms}
          textFieldProps={splitTextFieldProps}
          buttonRadius={
            !merchantConfig.use_rounded_edge_buttons ? "0px" : "10px"
          }
          buttonStyle={splitButtonStyle}
          buttonSx={buttonSx}
          arrowTop="-1px"
          termsTextStyle={splitTermsTextStyle}
          termsLinkStyle={splitTermsLinkStyle}
        />
        {merchantConfig && merchantConfig.is_pragma_branding_visible && (
          <Footer />
        )}
      </Grid>
    ),
    [
      buttonSx,
      control,
      errors,
      getHelperText,
      handleLoginSubmit,
      handleOpenTerms,
      loginFieldContent.light,
      merchantConfig,
      otpLoading,
      otpSendError,
      returnToStoreLink,
      rmsConfig.brand_home_page_note,
      rmsConfig.home_page_request_creation_title,
      showBrandLogo,
      splitButtonStyle,
      splitLoginAlignment,
      splitTermsLinkStyle,
      splitTermsTextStyle,
      splitTextFieldProps,
      theme.cover_image_visible_in_desktop,
    ],
  );

  const splitTerms = useMemo(
    () => (
      <Grid item xs={12} md={6} style={{ paddingTop: "50px" }}>
        <motion.div
          initial={{ opacity: 0, y: 200 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75 }}
          style={{
            color: "#333",
            padding: "0px 40px",
            justifyContent: "flex-start",
            background: "#fff",
          }}
        >
          <TermsContent {...desktopTermsProps} />
        </motion.div>
        {merchantConfig && merchantConfig.is_pragma_branding_visible && (
          <Footer />
        )}
      </Grid>
    ),
    [desktopTermsProps, merchantConfig],
  );

  const overlayContent = useMemo(
    () =>
      confirmOtp ? (
        <Otp
          inputValue={inputValue}
          setConfirmOtp={setConfirmOtp}
          response={response}
          setResponse={setResponse}
        />
      ) : (
        <div
          className="login-section"
          style={{
            background: `rgba(0,0,0,${theme.login_section_opacity})`,
            color: "#fff",
            padding: "2rem",
            border: "1px solid #fff",
            borderRadius: merchantConfig.use_rounded_edge_buttons
              ? "10px"
              : "0px",
            maxHeight: "max-content",
            minHeight: "100px",
            alignItems: overlayAlignment,
          }}
        >
          {returnToStoreLink}
          {showBrandLogo && (
            <LazyLoadImage
              effect="opacity"
              src={merchantConfig.brand_logo}
              className="brand-logo"
            />
          )}
          <LoginForm
            title={rmsConfig.home_page_request_creation_title}
            control={control}
            errors={errors}
            onSubmit={handleLoginSubmit}
            otpLoading={otpLoading}
            otpSendError={otpSendError}
            merchantConfig={merchantConfig}
            fieldContent={loginFieldContent.dark}
            variant="outlined"
            labelColor="#fff"
            helperTextResolver={getHelperText}
            brandHomePageNote={rmsConfig.brand_home_page_note}
            onOpenTerms={handleOpenTerms}
            textFieldProps={overlayTextFieldProps}
            buttonRadius={
              !merchantConfig.use_rounded_edge_buttons ? "0px" : "4px"
            }
            buttonStyle={overlayButtonStyle}
            arrowTop="-2px"
            termsTextStyle={overlayTermsTextStyle}
            termsLinkStyle={overlayTermsLinkStyle}
          />
          {merchantConfig && merchantConfig.is_pragma_branding_visible && (
            <Footer />
          )}
        </div>
      ),
    [
      confirmOtp,
      control,
      errors,
      getHelperText,
      handleLoginSubmit,
      handleOpenTerms,
      inputValue,
      loginFieldContent.dark,
      merchantConfig,
      otpLoading,
      otpSendError,
      overlayAlignment,
      overlayButtonStyle,
      overlayTermsLinkStyle,
      overlayTermsTextStyle,
      overlayTextFieldProps,
      response,
      returnToStoreLink,
      rmsConfig.brand_home_page_note,
      rmsConfig.home_page_request_creation_title,
      setConfirmOtp,
      setResponse,
      showBrandLogo,
      theme.login_section_opacity,
    ],
  );
  const splitContent = useMemo(() => {
    if (confirmOtp) {
      return (
        <Otp
          inputValue={inputValue}
          setConfirmOtp={setConfirmOtp}
          response={response}
          setResponse={setResponse}
        />
      );
    }

    return isDesktopTerms ? splitTerms : splitForm;
  }, [
    confirmOtp,
    inputValue,
    isDesktopTerms,
    response,
    setConfirmOtp,
    setResponse,
    splitForm,
    splitTerms,
  ]);

  return (
    <div>
      {loginLoader ||
      !loadConfigurations ||
      !hasValidEncipherencode(encipherencode) ? (
        <div
          style={{
            height: "100vh",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "-30px",
          }}
        >
          <ClipLoader
            color="#333"
            size={50}
            speedMultiplier={0.75}
            cssOverride={{ borderWidth: "6px" }}
          />
        </div>
      ) : (
        <StyledLogin
          themeColor={merchantConfig.primary_theme_color}
          coverImage={merchantConfig.brand_cover_image}
          desktopBrandImageVisibility={
            theme.login_section_brand_logo_visible_in_desktop
          }
          mobileBrandImageVisibility={
            theme.login_section_brand_logo_visible_in_mobile
          }
          desktopCoverImageVisibility={theme.cover_image_visible_in_desktop}
          mobileCoverImageVisibility={theme.cover_image_visible_in_mobile}
        >
          <LoginErrorBoundary>
            {isSplitLayout ? (
              <LoginLayoutSplit
                direction={splitDirection}
                justifyContent={splitJustifyContent}
                showHero={showHero}
                hero={brandImage}
                content={splitContent}
              />
            ) : (
              <LoginLayoutOverlay
                justifyContent={overlayJustifyContent}
                content={overlayContent}
              />
            )}
          </LoginErrorBoundary>
          {tnc && (
            <SwipeableDrawer
              anchor="bottom"
              open={window.innerWidth < 800 ? tnc : false}
              onClose={handleCloseTerms}
            >
              <StyledDrawerTnC>
                <TermsContent {...mobileTermsProps} />
              </StyledDrawerTnC>
            </SwipeableDrawer>
          )}
        </StyledLogin>
      )}
    </div>
  );
}

export default Login;
